import React, { useState, useMemo } from 'react';
import { Button } from '../../ui/button';
import { Heading } from '../../ui/heading';
import {
  Settings,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Building,
  Key,
} from 'lucide-react';
import { CONNECTOR_CONFIGS } from '../../../constants/connectors';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { Badge } from '../../ui/badge';
import DataEQConnectorConfigSheet from './DataEQConnectorConfigSheet';
import AddDataEQConnectorModal from './AddDataEQConnectorModal';
import { toast } from 'sonner';
import { useDataEQConnector, useSetDataEQConnectorActive } from '../../../hooks/useDataEQConnector';

// Predefined connector types - one of each type
interface ConnectorConfig {
  id: string;
  type: 'dataeq' | 'genesys' | 'icasework' | 'salesforce' | 'qualtrics';
  name: string;
  description: string;
  icon: React.ReactNode;
  status: 'not_configured' | 'active' | 'inactive' | 'error';
  requiresCredentials: boolean;
  color: string;
  lastSyncAt?: string;
  recordsIngested?: number;
  errorMessage?: string;
  configuration?: any;
}

const ConnectorsView: React.FC = () => {
  const [isAddConnectorModalOpen, setIsAddConnectorModalOpen] = useState(false);
  const [isConnectorSheetOpen, setIsConnectorSheetOpen] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<any>(null);
  const [selectedConnectorType, setSelectedConnectorType] = useState<string>('');

  // Fetch DataEQ connector data
  const { data: connectorData } = useDataEQConnector();
  const setConnectorActiveMutation = useSetDataEQConnectorActive();

  // Static list of other connector types (not yet implemented)
  const staticConnectors: ConnectorConfig[] = useMemo(() => [
    {
      id: 'genesys-1',
      type: 'genesys',
      name: 'Genesys Cloud',
      description: 'Contact center data and customer interaction analytics',
      icon: <Building className="w-5 h-5" />,
      status: 'not_configured',
      requiresCredentials: false,
      color: 'blue',
    },
    {
      id: 'icasework-1',
      type: 'icasework',
      name: 'iCaseWork',
      description: 'Case management system integration for tracking customer issues',
      icon: <Key className="w-5 h-5" />,
      status: 'not_configured',
      requiresCredentials: true,
      color: 'green',
    },
    {
      id: 'salesforce-1',
      type: 'salesforce',
      name: 'Salesforce Service Cloud',
      description: 'Customer service cases and support interactions from Salesforce Service Cloud',
      icon: <Building className="w-5 h-5" />,
      status: 'not_configured',
      requiresCredentials: true,
      color: 'orange',
    },
    {
      id: 'qualtrics-1',
      type: 'qualtrics',
      name: 'Qualtrics',
      description: 'Customer experience surveys and feedback data from Qualtrics Experience Management platform',
      icon: <Key className="w-5 h-5" />,
      status: 'not_configured',
      requiresCredentials: true,
      color: 'cyan',
    },
  ], []);

  // Merge real DataEQ connector with static connectors
  const connectors: ConnectorConfig[] = useMemo(() => {
    const config = CONNECTOR_CONFIGS.dataeq;

    // Always include DataEQ connector - either configured or not
    const dataeqConnector: ConnectorConfig = connectorData?.connector ? {
      // Connector is configured
      id: connectorData.connector.id,
      type: connectorData.connector.id as 'dataeq',
      name: connectorData.connector.name,
      description: config.description,
      icon: config.icon,
      status: connectorData?.dataset?.status || 'not_configured',
      requiresCredentials: config.requiresCredentials,
      color: config.color,
      lastSyncAt: connectorData.connector.lastSyncAt || undefined,
      recordsIngested: connectorData.connector.recordsIngested || 0,
      configuration: {
        connectorName: connectorData.connector.name,
        organisationCode: connectorData.connector.orgCode,
        organisationName: connectorData.connector.orgName,
        dataeqAccounts: connectorData.connector.dataeqAccounts,
      },
    } : {
      // Connector not configured yet
      id: 'dataeq-not-configured',
      type: 'dataeq',
      name: config.name,
      description: config.description,
      icon: config.icon,
      status: 'not_configured',
      requiresCredentials: config.requiresCredentials,
      color: config.color,
    };

    return [dataeqConnector, ...staticConnectors];
  }, [connectorData, staticConnectors]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inactive</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      case 'not_configured':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Not Configured</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getColorClasses = (color: string, status: string) => {
    if (status === 'not_configured') {
      return 'bg-gray-50 border-gray-200';
    }

    switch (color) {
      case 'purple':
        return 'bg-purple-50 border-purple-200';
      case 'blue':
        return 'bg-blue-50 border-blue-200';
      case 'green':
        return 'bg-green-50 border-green-200';
      case 'orange':
        return 'bg-orange-50 border-orange-200';
      case 'cyan':
        return 'bg-cyan-50 border-cyan-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  // Helper to check if connector is DataEQ-only and show toast if not
  const checkDataEQOnly = (connector: ConnectorConfig): boolean => {
    if (connector.type !== 'dataeq') {
      toast.info('Coming Soon', {
        description: `${connector.name} integration is not yet available.`,
      });
      return false;
    }
    return true;
  };

  const handleEnableConnector = (connector: ConnectorConfig) => {
    if (!checkDataEQOnly(connector)) return;

    setSelectedConnectorType(connector.type);
    setIsAddConnectorModalOpen(true);
  };

  const handleConfigureConnector = (connector: ConnectorConfig) => {
    // Convert ConnectorConfig to the format expected by ConnectorConfigSheet
    const connectorForSheet = {
      id: connector.id,
      type: connector.type,
      name: connector.name,
      status: connector.status === 'not_configured' ? 'draft' : connector.status,
      requiresCredentials: connector.requiresCredentials,
      createdBy: 'System',
      ...connector.configuration,
    };

    setSelectedConnector(connectorForSheet);
    setIsConnectorSheetOpen(true);
  };

  const handleConnectorCreated = (connectorData: any) => {
    // React Query will automatically refetch and update the connector
    toast.success('Connector Enabled', {
      description: `${connectorData.name || 'Connector'} has been successfully configured and enabled.`,
    });

    // Reset selection
    setSelectedConnectorType('');
  };

  const handleDisableConnector = async (connector: ConnectorConfig) => {
    if (!checkDataEQOnly(connector)) return;

    try {
      await setConnectorActiveMutation.mutateAsync(false);
      toast.success('Connector Disabled', {
        description: `${connector.name} has been successfully deactivated.`,
      });
    } catch (error) {
      toast.error('Failed to Disable', {
        description: 'An error occurred while disabling the connector.',
      });
      console.error('Disable connector error:', error);
    }
  };

  const handleActivateConnector = async (connector: ConnectorConfig) => {
    if (!checkDataEQOnly(connector)) return;

    try {
      await setConnectorActiveMutation.mutateAsync(true);
      toast.success('Connector Activated', {
        description: `${connector.name} has been successfully activated.`,
      });
    } catch (error) {
      toast.error('Failed to Activate', {
        description: 'An error occurred while activating the connector.',
      });
      console.error('Activate connector error:', error);
    }
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <Heading level={2} className="tracking-tight text-gray-900">Data Connectors</Heading>
              <p className="text-gray-600 mt-1">
                Configure automated data ingestion from external systems and platforms
              </p>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <div className="text-2xl font-bold">
                    {connectors.filter(c => c.status === 'active').length}
                  </div>
                  <div className="text-sm text-gray-600">Active</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <XCircle className="w-8 h-8 text-gray-400" />
                <div>
                  <div className="text-2xl font-bold">
                    {connectors.filter(c => c.status === 'inactive').length}
                  </div>
                  <div className="text-sm text-gray-600">Inactive</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-amber-500" />
                <div>
                  <div className="text-2xl font-bold">
                    {connectors.filter(c => c.status === 'not_configured').length}
                  </div>
                  <div className="text-sm text-gray-600">Not Configured</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-8 h-8 text-red-500" />
                <div>
                  <div className="text-2xl font-bold">
                    {connectors.filter(c => c.status === 'error').length}
                  </div>
                  <div className="text-sm text-gray-600">Errors</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Available Connectors List */}
        <Card>
          <CardHeader>
            <CardTitle>Available Connectors</CardTitle>
            <CardDescription>
              Configure your data integration connections
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-200">
              {connectors.map((connector) => (
                <div
                  key={connector.id}
                  className={`p-4 ${getColorClasses(connector.color, connector.status)} transition-all hover:bg-opacity-80`}
                >
                  <div className="flex items-center justify-between">
                    {/* Left side - Connector info */}
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`p-2 rounded-lg bg-white`}>
                        {connector.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-base font-semibold">{connector.name}</h3>
                          {getStatusBadge(connector.status)}
                        </div>
                        <p className="text-sm text-gray-600">
                          {connector.description}
                        </p>

                        {connector.errorMessage && (
                          <div className="text-red-600 text-sm mt-1">
                            Error: {connector.errorMessage}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Center - Action buttons */}
                    <div className="flex items-center gap-2">
                      {connector.status === 'not_configured' && (
                        <Button
                          onClick={() => handleEnableConnector(connector)}
                          size="sm"
                        >
                          Enable
                        </Button>
                      )}

                      {connector.status === 'active' && (
                        <>
                          <Button
                            onClick={() => handleConfigureConnector(connector)}
                            variant="outline"
                            size="sm"
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Configure
                          </Button>
                          <Button
                            onClick={() => handleDisableConnector(connector)}
                            variant="outline"
                            size="sm"
                          >
                            Disable
                          </Button>
                        </>
                      )}

                      {connector.status === 'inactive' && (
                        <>
                          <Button
                            onClick={() => handleActivateConnector(connector)}
                            size="sm"
                          >
                            Activate
                          </Button>
                          <Button
                            onClick={() => handleConfigureConnector(connector)}
                            variant="outline"
                            size="sm"
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Configure
                          </Button>
                        </>
                      )}

                      {connector.status === 'error' && (
                        <>
                          <Button
                            onClick={() => handleConfigureConnector(connector)}
                            variant="outline"
                            size="sm"
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Fix Configuration
                          </Button>
                          <Button
                            onClick={() => handleDisableConnector(connector)}
                            variant="outline"
                            size="sm"
                          >
                            Disable
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      {selectedConnectorType && (
        <AddDataEQConnectorModal
          open={isAddConnectorModalOpen}
          onOpenChange={setIsAddConnectorModalOpen}
          onConnectorCreated={handleConnectorCreated}
          preselectedType={selectedConnectorType}
        />
      )}

      <DataEQConnectorConfigSheet
        open={isConnectorSheetOpen}
        onOpenChange={setIsConnectorSheetOpen}
        connector={selectedConnector}
      />
    </div>
  );
};

export default ConnectorsView;